import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Space,
  message,
  Popconfirm,
  Tag,
  Typography,
  Row,
  Col,
  Select,
  Divider,
  Tooltip,
  Badge,
  Checkbox,
  Dropdown,
  Statistic,
} from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import type { MenuProps } from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  ReloadOutlined,
  SyncOutlined,
  SaveOutlined,
  ExportOutlined,
  DownloadOutlined,
  FileTextOutlined,
  CloudDownloadOutlined,
  UploadOutlined,
  FileExcelOutlined,
  MoreOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { authService } from '../services/authService';
import { companyService } from '../services/companyService';
import './common.css';
import { useI18n } from '../hooks/useI18n';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

// 添加 API_BASE_URL 常量（使用相对路径，避免类型与环境差异）
const API_BASE_URL = '/api';

// 辅助函数：创建带认证头的fetch请求
const createAuthenticatedFetch = (url: string, options: RequestInit = {}) => {
  const token = localStorage.getItem('token');
  const currentTenant = localStorage.getItem('currentTenant') || 'default';
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...options.headers as Record<string, string>
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  headers['X-Tenant-Code'] = currentTenant;
  
  return fetch(url, {
    ...options,
    headers
  });
};

interface Instruction {
  id: number;
  instruction: string;
  questions?: { id: number; question_text: string; sort_order: number }[] | string[];
  isDefault: boolean;
  category?: string;
  is_active?: number;
  is_synchronized: number;
  // project_id 来自后端，用于同步远程接口区分项目
  project_id?: number;
  scope?: string;
  created_at: string;
  updated_at: string;
}

type QuestionItem = { id?: number; question_text: string; sort_order?: number };

interface InstructionFormData {
  id?: number;
  instruction: string;
  questions: QuestionItem[];
  businessType: string;
  is_synchronized: number;
  scope?: string;
  project_id?: number;
}

const InstructionManager: React.FC = () => {
  const { t } = useI18n();
  const [instructions, setInstructions] = useState<Instruction[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingInstruction, setEditingInstruction] = useState<InstructionFormData | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  // 移除数据源状态
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBusinessType, setFilterBusinessType] = useState<string>('');
  const [filterIsSynchronized, setFilterIsSynchronized] = useState<string>('');

  // 页面项目筛选：全部 | projectId
  const [selectedProjectId, setSelectedProjectId] = useState<string>('all');
  // 当前租户下 projectId 列表（去重）
  const [projectIdOptions, setProjectIdOptions] = useState<string[]>([]);
  // tenant + projectId -> company_name（用于 project 下拉框 label 展示）
  const [projectIdToCompanyName, setProjectIdToCompanyName] = useState<Record<string, string>>({});

  const [syncingInstructionId, setSyncingInstructionId] = useState<number | null>(null);
  const [syncErrorInstructionId, setSyncErrorInstructionId] = useState<number | null>(null);
  const [syncErrorMessage, setSyncErrorMessage] = useState<string>('');
  const [batchSyncing, setBatchSyncing] = useState(false);
  const [form] = Form.useForm();
  
  // 多选相关状态
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedInstructions, setSelectedInstructions] = useState<Instruction[]>([]);
  
  // 备份相关状态
  const [backupLoading, setBackupLoading] = useState(false);
  const [backupFiles, setBackupFiles] = useState<any[]>([]);
  const [backupFilesVisible, setBackupFilesVisible] = useState(false);
  // 导入相关
  const [importVisible, setImportVisible] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importInputKey, setImportInputKey] = useState(0);
  // 是否为超级管理员
  const [isSuperAdmin, setIsSuperAdmin] = useState<boolean>(() => {
    try {
      const raw = localStorage.getItem('user');
      if (!raw) return false;
      const obj = JSON.parse(raw);
      return !!obj?.isSuperAdmin;
    } catch {
      return false;
    }
  });

  // 获取当前 tenant 对应的 tenant_id（优先从 localStorage.user.tenants 按当前 tenant_code 匹配）
  const getCurrentTenantId = (): string => {
    const currentTenantCode = localStorage.getItem('currentTenant') || 'default';
    try {
      const rawUser = localStorage.getItem('user');
      if (!rawUser) return String(currentTenantCode);
      const obj = JSON.parse(rawUser);
      const tenants = obj?.tenants;
      if (Array.isArray(tenants)) {
        const match = tenants.find(
          (t: any) => t?.code === currentTenantCode || t?.tenant_code === currentTenantCode
        );
        const id = match?.id ?? obj?.tenant_id ?? obj?.tenantId;
        if (id !== undefined && id !== null) return String(id);
      }
      const fallbackId = obj?.tenant_id ?? obj?.tenantId;
      return fallbackId !== undefined && fallbackId !== null ? String(fallbackId) : String(currentTenantCode);
    } catch {
      return String(currentTenantCode);
    }
  };
  
  // 分页相关状态
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // 加载数据
  const loadData = async () => {
    setLoading(true);
    try {
      const currentTenant = authService.getCurrentTenant() || 'default';
      
      // 从本地 instruction 路由读取数据
      const response = await createAuthenticatedFetch(
        `${API_BASE_URL}/${currentTenant}/instructions${isSuperAdmin ? '?isSuperAdmin=true' : ''}`,
        {
          headers: isSuperAdmin ? { 'x-is-super-admin': 'true' } : {}
        }
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch local instructions: ${response.status}`);
      }
      const result = await response.json();
      setInstructions(result.data?.instructions || []);

      // 计算当前租户下 projectId 列表（去重）
      const rawProjectIds = (result.data?.instructions || [])
        .map((i: any) => i?.project_id)
        .filter((v: any) => v !== undefined && v !== null);
      const unique: string[] = Array.from(new Set(rawProjectIds.map((v: any) => String(v))));
      setProjectIdOptions(unique);

      // 同 tenant 下：company.project_id -> company_name，用于替换 project 下拉框 label
      try {
        const companyResp = await companyService.getCompanies(currentTenant);
        const companies = companyResp.data || [];
        const map: Record<string, string> = {};

        unique.forEach((id) => {
          const matches = companies
            .filter((c: any) => c?.project_id !== undefined && c?.project_id !== null)
            .filter((c: any) => String(c.project_id) === id);

          if (matches.length > 0) {
            // 同 project_id 可能存在多家公司，展示全部（逗号拼接）
            map[id] = matches.map((m: any) => m?.company_name).filter(Boolean).join(', ');
          }
        });

        setProjectIdToCompanyName(map);
      } catch (e) {
        console.warn('Failed to load companies for project labels:', e);
        setProjectIdToCompanyName({});
      }

      // 如果当前选择的 projectId 不存在，则回退到“全部”
      if (selectedProjectId !== 'all' && !unique.includes(selectedProjectId)) {
        setSelectedProjectId('all');
      }

      message.success(t('instruction.loadSuccess'));
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('Failed to load instructions:', error);
      message.error(t('instruction.loadFailedWithReason', { reason: errorMessage }));
    } finally {
      setLoading(false);
    }
  };

  // 移除数据源切换功能

  // 创建新指令
  const handleCreate = () => {
    setIsEditMode(false);
    setEditingInstruction({
      instruction: '',
      questions: [],
      businessType: '',
      is_synchronized: 0,
      project_id: selectedProjectId !== 'all' ? Number(selectedProjectId) : 1,
    });
    form.setFieldsValue({
      id: undefined,
      instruction: '',
      questions: [],
      businessType: '',
      is_synchronized: 0
    });
    setModalVisible(true);
  };

  // 编辑指令
  const handleEdit = (instruction: Instruction) => {
    setIsEditMode(true);
    // 将 questions 映射为带 id 的结构（若无 id，则仅保留文本）
    const questionItems: QuestionItem[] = Array.isArray(instruction.questions) && instruction.questions.length > 0
      ? (typeof instruction.questions[0] === 'string'
          ? (instruction.questions as string[]).map((text, idx) => ({ question_text: text, sort_order: idx }))
          : (instruction.questions as any[]).map((q: any, idx: number) => ({ id: q.id, question_text: q.question_text, sort_order: q.sort_order ?? idx })) )
      : [];

    setEditingInstruction({
      id: instruction.id,
      instruction: instruction.instruction,
      questions: questionItems,
      businessType: instruction.category || '',
      is_synchronized: instruction.is_synchronized,
      scope: (instruction as any).scope || 'tenant',
      project_id: (instruction as any).project_id,
    });
    form.setFieldsValue({
      id: instruction.id,
      instruction: instruction.instruction,
      questions: questionItems,
      businessType: instruction.category || '',
      is_synchronized: instruction.is_synchronized,
      scope: (instruction as any).scope || 'tenant'
    });
    setModalVisible(true);
  };

  // 删除指令（软删除）
  const handleDelete = async (id: number) => {
    try {
      const currentTenant = authService.getCurrentTenant() || 'default';
      
      // 软删除本地指令
      const response = await createAuthenticatedFetch(`${API_BASE_URL}/${currentTenant}/instructions/${id}`, {
        method: 'DELETE'
      });
      if (!response.ok) {
        throw new Error(`Failed to delete local instruction: ${response.status}`);
      }
      
      message.success(t('instruction.deleteSuccess'));
      
      // 更新本地状态：标记为软删除和未同步
      setInstructions(prev => prev.map(inst => 
        inst.id === id 
          ? { ...inst, is_active: 0, is_synchronized: 0 }
          : inst
      ));
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('Failed to delete instruction:', error);
      message.error(t('instruction.deleteFailedWithReason', { reason: errorMessage }));
    }
  };

  // 同步指令
  const handleSync = async (instruction: Instruction) => {
    try {
      setSyncingInstructionId(instruction.id);
      const currentTenant = authService.getCurrentTenant() || 'default';

      const tenantId = getCurrentTenantId();
      // 同步时优先用页面右上角的 projectId 下拉框；否则回退到该指令自身的 project_id
      const projectId =
        selectedProjectId !== 'all'
          ? String(selectedProjectId)
          : String((instruction as any).project_id ?? 1);
      
      const response = await createAuthenticatedFetch(
        `${API_BASE_URL}/${currentTenant}/instructions/${instruction.id}/sync`,
        {
        method: 'POST',
          body: JSON.stringify({}), // 后端会自动从数据库获取服务器配置
          headers: {
            'X-Tenant-Id': tenantId,
            'X-Project-Id': projectId,
          },
        }
      );
      
      const result = await response.json();
      
      if (!response.ok) {
        // 如果响应不成功，尝试从响应体中获取错误信息
        const errorMessage = result.message || result.error || `HTTP ${response.status}: ${response.statusText}`;
        throw new Error(errorMessage);
      }
      
      if (result.success) {
        // 只显示通用的成功消息，避免显示后端可能包含中文的消息
        message.success(t('instruction.syncSuccess'));
        // 清除错误状态和错误消息
        setSyncErrorInstructionId(null);
        setSyncErrorMessage('');
        
        // 如果同步的指令是软删除状态（is_active=0），从列表中移除
        if (instruction.is_active === 0) {
          setInstructions(prev => prev.filter(inst => inst.id !== instruction.id));
          message.info(t('instruction.deletedInstructionSyncedAndRemoved'));
        } else {
          // 否则更新本地指令的同步状态
          setInstructions(prev => prev.map(inst => 
            inst.id === instruction.id 
              ? { ...inst, is_synchronized: 1 }
              : inst
          ));
        }
      } else {
        // 如果后端返回 success: false，显示错误信息
        const errorMessage = result.message || result.error || t('instruction.syncFailedUnknown');
        message.error(t('instruction.syncFailedWithReason', { reason: errorMessage }));
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('Failed to sync instruction:', error);
      
      // 设置错误状态和错误消息
      setSyncErrorInstructionId(instruction.id);
      setSyncErrorMessage(t('instruction.syncFailedLine', { name: instruction.instruction.substring(0, 50), reason: errorMessage }));
      
      // 同时显示一个通知
      message.warning(t('instruction.checkServerAndNetwork'));
      
      // 3秒后清除错误状态
      setTimeout(() => {
        setSyncErrorInstructionId(null);
      }, 3000);
    } finally {
      setSyncingInstructionId(null);
    }
  };

  // 保存指令
  const handleSave = async (values: Record<string, unknown>) => {
    try {
      const currentTenant = authService.getCurrentTenant() || 'default';

      const projectIdToSave =
        editingInstruction?.project_id !== undefined && editingInstruction?.project_id !== null
          ? editingInstruction.project_id
          : selectedProjectId !== 'all'
            ? Number(selectedProjectId)
            : 1;
      
      // 过滤空的问题，保留可能的 id
      const questions = values.questions as QuestionItem[] | string[];
      const normalized: QuestionItem[] = (questions as any[]).map((q: any, idx: number) => {
        if (typeof q === 'string') {
          return { question_text: q.trim(), sort_order: idx };
        }
        return { id: q?.id, question_text: (q?.question_text || '').trim(), sort_order: q?.sort_order ?? idx };
      }).filter(q => q.question_text.length > 0);
      
      const instructionData = {
        instruction: values.instruction,
        category: values.businessType,
        questions: normalized,
        project_id: projectIdToSave,
        scope: values.scope || (isSuperAdmin ? 'system' : 'tenant'),
        is_synchronized: values.is_synchronized || 0
      };

      if (isEditMode && editingInstruction?.id) {
        // 更新本地指令 - 后端期望 { update_data: {...} }
        const response = await createAuthenticatedFetch(`${API_BASE_URL}/${currentTenant}/instructions/${editingInstruction.id}`, {
          method: 'PUT',
          body: JSON.stringify({ update_data: instructionData })
        });
        if (!response.ok) {
          const result = await response.json();
          throw new Error(result.message || `Failed to update: ${response.status}`);
        }
      } else {
        // 创建本地指令 - 后端期望 { instruction_data: {...} }
        const response = await createAuthenticatedFetch(`${API_BASE_URL}/${currentTenant}/instructions`, {
          method: 'POST',
          body: JSON.stringify({ 
            instruction_data: {
              ...instructionData,
              tenant_code: currentTenant
            }
          })
        });
        if (!response.ok) {
          const result = await response.json();
          throw new Error(result.message || `Failed to create: ${response.status}`);
        }
      }
      
      message.success(isEditMode ? t('instruction.updateSuccess') : t('instruction.createSuccess'));
      setModalVisible(false);
      loadData();
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('Failed to save instruction:', error);
      message.error(t('instruction.saveFailedWithReason', { reason: errorMessage }));
    }
  };



  // 备份指令配置
  const handleBackupInstructions = async () => {
    setBackupLoading(true);
    try {
      const currentTenant = localStorage.getItem('currentTenant') || 'default';
      const response = await createAuthenticatedFetch(`${API_BASE_URL}/${currentTenant}/instructions/backup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const result = await response.json();
      if (result.success) {
        message.success(t('instruction.backupSuccess', { filename: result.data.filename }));
        if (isSuperAdmin) loadBackupFiles();
      } else {
        message.error(t('instruction.backupFailed'));
      }
    } catch (error) {
      console.error('Failed to backup instructions:', error);
      message.error(t('instruction.backupFailed'));
    } finally {
      setBackupLoading(false);
    }
  };

  // 备份远程指令
  const handleBackupRemoteInstructions = async () => {
    setBackupLoading(true);
    try {
      const currentTenant = localStorage.getItem('currentTenant') || 'default';
      const response = await createAuthenticatedFetch(`${API_BASE_URL}/${currentTenant}/instructions/backup-remote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const result = await response.json();
      if (result.success) {
        message.success(t('instruction.backupSuccess', { filename: result.data?.filename || '' }));
        if (isSuperAdmin) loadBackupFiles();
      } else {
        message.error(t('instruction.backupFailed'));
      }
    } catch (error) {
      console.error('Failed to backup remote instructions:', error);
      message.error(t('instruction.backupFailed'));
    } finally {
      setBackupLoading(false);
    }
  };

  // 导入（上传JSON并更新本地数据库）
  const handleImportFromJson = async () => {
    if (!importFile) {
      message.info(t('instruction.noFileSelected'));
      return;
    }
    setImporting(true);
    try {
      const text = await importFile.text();
      const json = JSON.parse(text);
      
      const currentTenant = authService.getCurrentTenant() || 'default';
      
      // 直接调用后端导入接口，传递解析后的 JSON
      const resp = await createAuthenticatedFetch(`${API_BASE_URL}/${currentTenant}/instructions/import`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(json)
      });
      
      const result = await resp.json();
      if (!resp.ok || !result.success) {
        throw new Error(result.message || String(resp.status));
      }
      
      const stats = result.data 
        ? ` (${t('instruction.importStats', { created: result.data.created || 0, updated: result.data.updated || 0, failed: result.data.failed || 0 })})`
        : '';
      message.success(t('instruction.importCompleted') + stats);
      setImportVisible(false);
      setImportFile(null);
      setImportInputKey(k => k + 1);
      await loadData();
    } catch (e: any) {
      message.error(e?.message || t('instruction.importFailed'));
    } finally {
      setImporting(false);
    }
  };

  // 导入默认文件（工程/data/initialize/instructions/instructions.json）
  const handleImportDefault = async () => {
    setImporting(true);
    try {
      const currentTenant = authService.getCurrentTenant() || 'default';
      const resp = await createAuthenticatedFetch(`${API_BASE_URL}/${currentTenant}/instructions/import`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ useDefault: true })
      });
      const result = await resp.json();
      if (!resp.ok || !result.success) {
        throw new Error(result.message || String(resp.status));
      }
      const stats = result.data 
        ? ` (${t('instruction.importStats', { created: result.data.created || 0, updated: result.data.updated || 0, failed: result.data.failed || 0 })})`
        : '';
      message.success(t('instruction.importDefaultCompleted') + stats);
      setImportVisible(false);
      setImportFile(null);
      setImportInputKey(k => k + 1);
      await loadData();
    } catch (e: any) {
      message.error(e?.message || t('instruction.importDefaultFailed'));
    } finally {
      setImporting(false);
    }
  };

  // 加载备份文件列表
  const loadBackupFiles = async () => {
    try {
      const currentTenant = localStorage.getItem('currentTenant') || 'default';
      const response = await createAuthenticatedFetch(`${API_BASE_URL}/${currentTenant}/instructions/backup/files`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const result = await response.json();
      if (result.success) {
        setBackupFiles(result.data);
      }
    } catch (error) {
      console.error('Failed to load backup files:', error);
    }
  };

  // 下载备份文件
  const handleDownloadBackupFile = async (filename: string) => {
    try {
      const currentTenant = localStorage.getItem('currentTenant') || 'default';
      const response = await createAuthenticatedFetch(`${API_BASE_URL}/${currentTenant}/instructions/backup/${filename}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const data = await response.json();
      
      // 创建下载链接
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      message.success(t('instruction.fileDownloadSuccess'));
    } catch (error) {
      console.error('Failed to download file:', error);
      message.error(t('instruction.fileDownloadFailed'));
    }
  };

  // 下载备份文件为Excel格式
  const handleDownloadBackupFileAsExcel = async (filename: string) => {
    try {
      const currentTenant = localStorage.getItem('currentTenant') || 'default';
      const response = await createAuthenticatedFetch(`${API_BASE_URL}/${currentTenant}/instructions/backup/${filename}/excel`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('下载失败');
      }
      
      // 获取文件名
      const contentDisposition = response.headers.get('Content-Disposition');
      let downloadFilename = filename.replace('.json', '.xlsx');
      if (contentDisposition) {
        const matches = contentDisposition.match(/filename="(.+)"/);
        if (matches) {
          downloadFilename = decodeURIComponent(matches[1]);
        }
      }
      
      // 创建下载链接
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      message.success(t('instruction.excelDownloadSuccess'));
    } catch (error) {
      console.error('Failed to download Excel file:', error);
      message.error(t('instruction.excelDownloadFailed'));
    }
  };

  // 显示备份文件列表
  const handleShowBackupFiles = () => {
    setBackupFilesVisible(true);
    loadBackupFiles();
  };

  // 导出数据（保留原有功能）
  const exportData = () => {
    const exportData = {
      data: {
        instructions: instructions
      }
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `instructions-local-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
    
    message.success(t('instruction.dataExportSuccess'));
  };

  // 批量操作相关函数
  const handleBatchSync = async () => {
    if (selectedInstructions.length === 0) {
      message.warning(t('instruction.selectInstructionsToSync'));
      return;
    }
    if (batchSyncing) return;
    setBatchSyncing(true);

    const unsyncedInstructions = selectedInstructions.filter((instruction: Instruction) => instruction.is_synchronized === 0);
    
    if (unsyncedInstructions.length === 0) {
      message.warning(t('instruction.allSelectedAlreadySynced'));
      return;
    }

    try {
      message.loading(t('instruction.batchSyncing', { count: unsyncedInstructions.length }), 0);

      // 串行同步，避免一次性打爆后端（与 ModelingManager 保持一致）
      for (const instruction of unsyncedInstructions) {
        await handleSync(instruction);
      }
      
      message.destroy();
      message.success(t('instruction.batchSyncSuccess', { count: unsyncedInstructions.length }));
      
      // 清空选择
      setSelectedRowKeys([]);
      setSelectedInstructions([]);
    } catch {
      message.destroy();
      message.error(t('instruction.batchSyncFailed'));
    } finally {
      setBatchSyncing(false);
    }
  };

  const handleBatchDelete = async () => {
    if (selectedInstructions.length === 0) {
      message.warning(t('instruction.selectInstructionsToDelete'));
      return;
    }

    try {
      const currentTenant = authService.getCurrentTenant() || 'default';
      const ids = selectedInstructions.map(inst => inst.id);
      
      const response = await createAuthenticatedFetch(`${API_BASE_URL}/${currentTenant}/instructions/batch-delete`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ ids })
      });
      
      const result = await response.json();
      if (result.success) {
        // 只显示前端的国际化消息，避免显示后端可能包含中文的消息
        message.success(t('instruction.batchDeleteCompleted'));
      } else {
        message.error(t('instruction.batchDeleteFailed'));
      }
      
      // 清空选择
      setSelectedRowKeys([]);
      setSelectedInstructions([]);
      
      // 重新加载数据
      loadData();
    } catch (error) {
      console.error('批量删除失败:', error);
      message.error(t('instruction.batchDeleteFailed'));
    }
  };

  const handleBatchForceDelete = async () => {
    if (selectedInstructions.length === 0) {
      message.warning(t('instruction.selectInstructionsToForceDelete'));
      return;
    }

    try {
      const currentTenant = authService.getCurrentTenant() || 'default';
      const ids = selectedInstructions.map(inst => inst.id);
      
      const response = await createAuthenticatedFetch(`${API_BASE_URL}/${currentTenant}/instructions/batch-force-delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids })
      });
      
      const result = await response.json();
      if (result.success) {
        // 只显示前端的国际化消息，避免显示后端可能包含中文的消息
        message.success(t('instruction.batchForceDeleteCompleted'));
      } else {
        message.error(t('instruction.batchForceDeleteFailed'));
      }
      
      // 清空选择
      setSelectedRowKeys([]);
      setSelectedInstructions([]);
      
      // 重新加载数据
      loadData();
    } catch (error) {
      console.error('批量强制删除失败:', error);
      message.error(t('instruction.batchForceDeleteFailed'));
    }
  };

  const handleBatchExport = () => {
    if (selectedInstructions.length === 0) {
      message.warning(t('instruction.selectInstructionsToExport'));
      return;
    }

    const exportData = {
      data: {
        instructions: selectedInstructions
      }
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `instructions-selected-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
    
    message.success(t('instruction.batchExportSuccess', { count: selectedInstructions.length }));
  };
  
  const handleForceDelete = async (id: number) => {
    try {
      const currentTenant = authService.getCurrentTenant() || 'default';
      
      const response = await createAuthenticatedFetch(`${API_BASE_URL}/${currentTenant}/instructions/${id}/force`, {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to force delete instruction: ${response.status}`);
      }
      
      message.success(t('instruction.forceDeletedSuccess'));
      loadData();
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('Failed to force delete instruction:', error);
      message.error(t('instruction.forceDeleteFailedWithReason', { reason: errorMessage }));
    }
  };

  // 过滤数据
  const filteredInstructions = instructions.filter(instruction => {
    const questionsTextList = Array.isArray(instruction.questions)
      ? (typeof instruction.questions[0] === 'string'
          ? (instruction.questions as string[])
          : (instruction.questions as any[]).map((q: any) => q.question_text))
      : [];
    const matchesSearch = !searchTerm || 
      instruction.instruction.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (questionsTextList && questionsTextList.some((q: string) => q.toLowerCase().includes(searchTerm.toLowerCase())));
    
    const matchesBusinessType = !filterBusinessType || instruction.category === filterBusinessType;
    const matchesIsSynchronized = filterIsSynchronized === '' || 
      (filterIsSynchronized === 'true' ? instruction.is_synchronized === 1 : instruction.is_synchronized === 0);

    const projectIdValue =
      instruction.project_id !== undefined && instruction.project_id !== null
        ? String(instruction.project_id)
        : '1';
    const matchesProjectId = selectedProjectId === 'all' ? true : projectIdValue === selectedProjectId;
    
    return matchesSearch && matchesBusinessType && matchesIsSynchronized && matchesProjectId;
  });

  const activeInstructions = instructions.filter(i => i.is_active !== 0);
  const syncedInstructions = instructions.filter(i => i.is_synchronized === 1);
  const pendingInstructions = instructions.filter(i => i.is_synchronized !== 1);

  // 获取业务类型标签颜色
  const getBusinessTypeColor = (businessType: string) => {
    const colors: Record<string, string> = {
      'Sales': 'blue',
      'Purchase': 'green',
      'Inventory': 'orange',
      'Finance': 'purple',
      'General': 'default',
      'Production': 'magenta',
      'System': 'geekblue',
      'Localization': 'cyan'
    };
    return colors[businessType] || 'default';
  };



  // 表格列定义
  const columns = [
    {
      title: (
        <Checkbox
          checked={selectedRowKeys.length === filteredInstructions.length && filteredInstructions.length > 0}
          indeterminate={selectedRowKeys.length > 0 && selectedRowKeys.length < filteredInstructions.length}
          onChange={(e: CheckboxChangeEvent) => {
            if (e.target.checked) {
              setSelectedRowKeys(filteredInstructions.map(inst => inst.id));
              setSelectedInstructions(filteredInstructions);
            } else {
              setSelectedRowKeys([]);
              setSelectedInstructions([]);
            }
          }}
        />
      ),
      dataIndex: 'id',
      key: 'select',
      width: 60,
      render: (id: number, record: Instruction) => (
        <Checkbox
          checked={selectedRowKeys.includes(id)}
          onChange={(e: CheckboxChangeEvent) => {
            if (e.target.checked) {
              setSelectedRowKeys([...selectedRowKeys, id]);
              setSelectedInstructions([...selectedInstructions, record]);
            } else {
              setSelectedRowKeys(selectedRowKeys.filter(key => key !== id));
              setSelectedInstructions(selectedInstructions.filter(instruction => instruction.id !== id));
            }
          }}
        />
      ),
    },
    {
      title: t('instruction.colId'),
      dataIndex: 'id',
      key: 'id',
      width: 80,
      sorter: (a: Instruction, b: Instruction) => a.id - b.id,
      defaultSortOrder: 'ascend' as const,
      render: (id: number) => <Tag color="blue">{id}</Tag>,
    },
    {
      title: t('instruction.colInstruction'),
      dataIndex: 'instruction',
      key: 'instruction',
      width: 300,
      sorter: (a: Instruction, b: Instruction) => a.instruction.localeCompare(b.instruction),
      render: (instruction: string) => (
        <Tooltip 
          title={instruction}
          overlayStyle={{ maxWidth: '500px' }}
          overlayInnerStyle={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
        >
          <div style={{ maxWidth: '280px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {instruction}
          </div>
        </Tooltip>
      ),
    },
    {
      title: t('instruction.colQuestionCount'),
      dataIndex: 'questions',
      key: 'questionCount',
      width: 100,
      sorter: (a: Instruction, b: Instruction) => (a.questions?.length || 0) - (b.questions?.length || 0),
      render: (questions: any[] | undefined) => (
        <Badge count={questions?.length || 0} showZero style={{ backgroundColor: '#52c41a' }} />
      ),
    },
    {
      title: t('instruction.colQuestionPreview'),
      dataIndex: 'questions',
      key: 'questions',
      width: 200,
      render: (questions: any[] | undefined) => {
        const list = questions
          ? (typeof (questions[0] as any) === 'string' ? (questions as string[]) : (questions as any[]).map(q => q.question_text))
          : undefined;
        const questionsText = list && list.length > 0 
          ? list.map((q, index) => `${index + 1}. ${q}`).join('\n\n')
          : t('instruction.noQuestions');
        
        return (
          <Tooltip 
            title={questionsText}
            overlayStyle={{ maxWidth: '500px' }}
            overlayInnerStyle={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
          >
            <div style={{ maxHeight: '60px', overflow: 'hidden' }}>
              {list && list.length > 0 ? (
                <>
                  {list.slice(0, 2).map((question, index) => (
                    <div key={index} style={{ fontSize: '12px', marginBottom: '2px' }}>
                      {question.length > 30 ? question.substring(0, 30) + '...' : question}
                    </div>
                  ))}
                  {list.length > 2 && (
                    <Text type="secondary" style={{ fontSize: '11px' }}>
                      {`+${list.length - 2} `}{t('instruction.moreQuestions')}
                    </Text>
                  )}
                </>
              ) : (
                <Text type="secondary" style={{ fontSize: '12px' }}>{t('instruction.noQuestions')}</Text>
              )}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: t('instruction.colBusinessType'),
      dataIndex: 'category',
      key: 'businessType',
      width: 120,
      sorter: (a: Instruction, b: Instruction) => (a.category || '').localeCompare(b.category || ''),
      render: (businessType: string) => (
        businessType ? (
          <Tag color={getBusinessTypeColor(businessType)}>{businessType}</Tag>
        ) : (
          <Text type="secondary">{t('instruction.notSet')}</Text>
        )
      ),
    },

    // 移除默认状态列
    {
      title: t('instruction.colSyncStatus'),
      dataIndex: 'is_synchronized',
      key: 'isSynchronized',
      width: 120,
      sorter: (a: Instruction, b: Instruction) => a.is_synchronized - b.is_synchronized,
      render: (isSynchronized: number, record: Instruction) => {
        // 如果当前指令正在同步失败状态
        if (record.id === syncErrorInstructionId) {
          return <Tag color="red">{t('instruction.syncFailed')}</Tag>;
        }
        // 如果当前指令正在同步中
        if (record.id === syncingInstructionId) {
          return <Tag color="processing">{t('instruction.syncing')}</Tag>;
        }
        // 正常状态
        return (
          <Tag color={isSynchronized === 1 ? 'green' : 'orange'}>
            {isSynchronized === 1 ? t('instruction.synced') : t('instruction.unsynced')}
          </Tag>
        );
      },
    },
    {
      title: t('instruction.colUpdatedAt'),
      dataIndex: 'updated_at',
      key: 'updatedAt',
      width: 150,
      sorter: (a: Instruction, b: Instruction) => {
        const aTime = a.updated_at ? new Date(a.updated_at).getTime() : 0;
        const bTime = b.updated_at ? new Date(b.updated_at).getTime() : 0;
        return aTime - bTime;
      },
      render: (updatedAt: string) => (
        <Text type="secondary" style={{ fontSize: '12px' }}>
          {updatedAt ? new Date(updatedAt).toLocaleString() : t('instruction.unknown')}
        </Text>
      ),
    },
    ...(isSuperAdmin ? [{
      title: 'Scope',
      dataIndex: 'scope',
      key: 'scope',
      width: 100,
      render: (scope: string) => (
        <Tag color={scope === 'system' ? 'blue' : 'green'}>
          {scope || 'tenant'}
        </Tag>
      ),
    }] : []),
    {
      title: t('common.actions'),
      key: 'actions',
      width: 180,
      fixed: 'right' as const,
      render: (_: unknown, record: Instruction) => {
        const deleteMenuItems: MenuProps['items'] = [
          {
            key: 'delete',
            icon: <DeleteOutlined />,
            label: t('common.delete'),
            danger: true,
            onClick: () => {
              Modal.confirm({
                title: t('instruction.confirmDelete'),
                content: t('instruction.cannotUndo'),
                okText: t('instruction.confirmDelete'),
                cancelText: t('common.cancel'),
                okButtonProps: { danger: true },
                onOk: () => handleDelete(record.id)
              });
            }
          },
          {
            key: 'forceDelete',
            icon: <DeleteOutlined />,
            label: t('instruction.forceDelete'),
            danger: true,
            onClick: () => {
              Modal.confirm({
                title: t('instruction.confirmForceDelete'),
                content: t('instruction.forceDeleteWarning'),
                okText: t('instruction.confirmForceDelete'),
                cancelText: t('common.cancel'),
                okButtonProps: { danger: true },
                onOk: () => handleForceDelete(record.id)
              });
            }
          }
        ];

        return (
          <Space size="small">
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            >
              {t('common.edit')}
            </Button>
            <Button
              type="link"
              size="small"
              icon={<SyncOutlined spin={record.id === syncingInstructionId} />}
              onClick={() => handleSync(record)}
              loading={record.id === syncingInstructionId}
              danger={record.id === syncErrorInstructionId}
            >
              {record.id === syncingInstructionId ? t('instruction.syncing') : 
               record.id === syncErrorInstructionId ? t('instruction.retry') : t('instruction.sync')}
            </Button>
            <Dropdown menu={{ items: deleteMenuItems }} trigger={['click']}>
              <Button type="link" size="small" danger icon={<DeleteOutlined />} />
            </Dropdown>
          </Space>
        );
      },
    },
  ];

  // 初始化加载数据
  useEffect(() => {
    loadData();
  }, []);

  // 监听租户变化，重新加载数据
  useEffect(() => {
    const handleTenantChange = () => {
      loadData();
    };

    // 监听localStorage变化事件
    window.addEventListener('storage', handleTenantChange);
    
    // 监听自定义的租户变化事件
    window.addEventListener('tenantChanged', handleTenantChange);

    return () => {
      window.removeEventListener('storage', handleTenantChange);
      window.removeEventListener('tenantChanged', handleTenantChange);
    };
  }, []);

  // 切换 projectId 时清空当前选择的行，避免 batch 同步跨项目
  useEffect(() => {
    setSelectedRowKeys([]);
    setSelectedInstructions([]);
  }, [selectedProjectId]);

  return (
    <div style={{ padding: '24px' }}>
      {syncErrorMessage && (
        <div style={{ 
          marginBottom: 16, 
          padding: '12px 16px', 
          backgroundColor: '#fff2f0', 
          border: '1px solid #ffccc7', 
          borderRadius: 6,
          position: 'relative'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Text type="danger" style={{ marginRight: 8 }}>
                <SyncOutlined style={{ marginRight: 4 }} />
                {t('instruction.syncFailed')}
              </Text>
              <Text type="secondary">{syncErrorMessage}</Text>
            </div>
            <Button 
              type="text" 
              size="small" 
              onClick={() => setSyncErrorMessage('')}
              style={{ color: '#999' }}
            >
              ✕
            </Button>
          </div>
        </div>
      )}

      <div style={{ marginBottom: '24px' }}>
        <Row align="middle" justify="start" style={{ flexWrap: 'wrap' }}>
          <Space align="baseline" size={16} style={{ flexWrap: 'wrap' }}>
            <Title level={2} style={{ margin: 0 }}>
              <FileTextOutlined style={{ marginRight: '8px' }} />
              {t('instruction.title')}
            </Title>
            <Select
              style={{ width: 240 }}
              value={selectedProjectId}
              onChange={(value) => setSelectedProjectId(value)}
              options={[
                { value: 'all', label: t('common.all') },
                ...projectIdOptions.map(id => ({
                  value: id,
                  label: projectIdToCompanyName[id] || `ProjectId: ${id}`
                })),
              ]}
            />
            <Text type="secondary">Version: —</Text>
            <Text type="secondary">{t('instruction.subtitle')}</Text>
            <Space style={{ flexWrap: 'wrap' }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCreate}
              >
                {t('instruction.create')}
              </Button>
              <Button
                icon={<DownloadOutlined />}
                loading={backupLoading}
                onClick={handleBackupInstructions}
              >
                {t('instruction.backup')}
              </Button>
              <Button
                icon={<CloudDownloadOutlined />}
                loading={backupLoading}
                onClick={handleBackupRemoteInstructions}
              >
                {t('instruction.backupRemote')}
              </Button>
              {isSuperAdmin && (
                <Button
                  icon={<FileTextOutlined />}
                  onClick={handleShowBackupFiles}
                >
                  {t('instruction.backupFiles')}
                </Button>
              )}
              <Button
                icon={<UploadOutlined />}
                onClick={() => setImportVisible(true)}
              >
                {t('instruction.import')}
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={loadData}
                loading={loading}
              >
                {t('instruction.refreshData')}
              </Button>
              <Button
                icon={<ExportOutlined />}
                onClick={exportData}
              >
                {t('instruction.exportData')}
              </Button>
            </Space>
          </Space>
        </Row>
      </div>

      <Row gutter={16} align="stretch" style={{ marginBottom: '24px' }}>
        <Col span={24}>
          <Card
            style={{
              minHeight: 120,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Space size={48} align="center" wrap>
              <Statistic
                title={t('instruction.stat.total')}
                value={instructions.length}
                prefix={<FileTextOutlined />}
              />
              <Statistic
                title={t('instruction.stat.active')}
                value={activeInstructions.length}
                valueStyle={{ color: '#3f8600' }}
                prefix={<CheckCircleOutlined />}
              />
              <Statistic
                title={t('instruction.stat.synced')}
                value={syncedInstructions.length}
                valueStyle={{ color: '#1890ff' }}
                prefix={<SyncOutlined />}
              />
              <Statistic
                title={t('instruction.stat.pending')}
                value={pendingInstructions.length}
                valueStyle={{ color: '#faad14' }}
                prefix={<ExclamationCircleOutlined />}
              />
            </Space>
          </Card>
        </Col>
      </Row>

      <Card>
        <Space style={{ marginBottom: 16 }} wrap>
          <Input
            allowClear
            style={{ width: 260 }}
            placeholder={t('instruction.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <Tooltip title={t('instruction.filter.businessTypeTip')} placement="top">
            <Select
              placeholder={t('instruction.filter.businessTypePlaceholder')}
              value={filterBusinessType || undefined}
              onChange={(v) => setFilterBusinessType(v ?? '')}
              allowClear
              style={{ width: 200 }}
            >
              <Option value="Sales">{t('instruction.category.sales')}</Option>
              <Option value="Purchase">{t('instruction.category.purchase')}</Option>
              <Option value="Inventory">{t('instruction.category.inventory')}</Option>
              <Option value="Finance">{t('instruction.category.finance')}</Option>
              <Option value="Production">{t('instruction.category.production')}</Option>
              <Option value="System">{t('instruction.category.system')}</Option>
              <Option value="Localization">{t('instruction.category.localization')}</Option>
              <Option value="General">{t('instruction.category.general')}</Option>
            </Select>
          </Tooltip>
          <Tooltip title={t('instruction.filter.syncStatusTip')} placement="top">
            <Select
              placeholder={t('instruction.filter.syncStatusPlaceholder')}
              value={filterIsSynchronized || undefined}
              onChange={(v) => setFilterIsSynchronized(v ?? '')}
              allowClear
              style={{ width: 180 }}
            >
              <Option value="true">{t('instruction.synced')}</Option>
              <Option value="false">{t('instruction.unsynced')}</Option>
            </Select>
          </Tooltip>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              setSearchTerm('');
              setFilterBusinessType('');
              setFilterIsSynchronized('');
            }}
          >
            {t('instruction.resetFilters')}
          </Button>
        </Space>

        {selectedRowKeys.length > 0 && (
          <div
            style={{
              marginBottom: 16,
              padding: '12px 16px',
              backgroundColor: '#f6ffed',
              border: '1px solid #b7eb8f',
              borderRadius: 8,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
              <Text type="secondary">{t('instruction.selectedCount', { count: selectedRowKeys.length })}</Text>
              <Space wrap>
                <Button 
                  type="primary"
                  icon={<SyncOutlined />}
                  onClick={handleBatchSync}
                  loading={loading || batchSyncing}
                >
                  {t('instruction.batchSync')}
                </Button>
                <Popconfirm
                  title={t('instruction.confirmBatchDelete')}
                  description={t('instruction.cannotUndo')}
                  onConfirm={handleBatchDelete}
                  okText={t('instruction.confirmDelete')}
                  cancelText={t('common.cancel')}
                  okButtonProps={{ danger: true }}
                  placement="topRight"
                >
                  <Button 
                    danger
                    icon={<DeleteOutlined />}
                    loading={loading}
                  >
                    {t('instruction.batchDelete')}
                  </Button>
                </Popconfirm>
                <Popconfirm
                  title={t('instruction.confirmBatchForceDelete')}
                  description={t('instruction.forceDeleteWarning')}
                  onConfirm={handleBatchForceDelete}
                  okText={t('instruction.confirmForceDelete')}
                  cancelText={t('common.cancel')}
                  okButtonProps={{ danger: true }}
                  placement="topRight"
                >
                  <Button 
                    danger
                    icon={<DeleteOutlined />}
                    loading={loading}
                    style={{ borderColor: '#cf1322', color: '#cf1322' }}
                  >
                    {t('instruction.batchForceDelete')}
                  </Button>
                </Popconfirm>
                <Button 
                  icon={<ExportOutlined />}
                  onClick={handleBatchExport}
                >
                  {t('instruction.batchExport')}
                </Button>
              </Space>
            </div>
          </div>
        )}

        <Table
          columns={columns}
          dataSource={filteredInstructions}
          loading={loading}
          rowKey="id"
          rowClassName={(record: Instruction) => record.is_active === 0 ? 'row-deleted' : ''}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => t('instruction.paginationTotal', { total, from: range[0], to: range[1] }),
            pageSizeOptions: ['10', '20', '50', '100'],
            onChange: (page, size) => {
              setCurrentPage(page);
              setPageSize(size);
            },
            onShowSizeChange: (current, size) => {
              setCurrentPage(1);
              setPageSize(size);
            }
          }}
          scroll={{ x: 1200 }}
          size="middle"
        />
      </Card>

      {/* 编辑/创建模态框 */}
      <Modal
        title={isEditMode ? t('instruction.modal.editTitle') : t('instruction.modal.createTitle')}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={800}
        centered
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
          initialValues={editingInstruction || undefined}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="ID"
                name="id"
              >
                <Input disabled placeholder={t('instruction.autoGenerated')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={t('instruction.businessType')}
                name="businessType"
                rules={[{ required: true, message: t('instruction.enterBusinessType') }]}
              >
                <Select placeholder={t('instruction.filter.businessTypePlaceholder')}>
                  <Option value="Sales">{t('instruction.category.sales')}</Option>
                  <Option value="Purchase">{t('instruction.category.purchase')}</Option>
                  <Option value="Inventory">{t('instruction.category.inventory')}</Option>
                  <Option value="Finance">{t('instruction.category.finance')}</Option>
                  <Option value="Production">{t('instruction.category.production')}</Option>
                  <Option value="System">{t('instruction.category.system')}</Option>
                  <Option value="Localization">{t('instruction.category.localization')}</Option>
                  <Option value="General">{t('instruction.category.general')}</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={t('instruction.syncStatus')}
                name="is_synchronized"
              >
                <Select placeholder={t('instruction.selectSyncStatus')}>
                  <Option value={1}>{t('instruction.synced')}</Option>
                  <Option value={0}>{t('instruction.unsynced')}</Option>
                </Select>
              </Form.Item>
            </Col>
            {isSuperAdmin && (
              <Col span={12}>
                <Form.Item
                  label="Scope"
                  name="scope"
                  initialValue="tenant"
                >
                  <Select placeholder="选择Scope">
                    <Option value="system">System</Option>
                    <Option value="tenant">Tenant</Option>
                  </Select>
                </Form.Item>
              </Col>
            )}
          </Row>

          <Form.Item
            label={t('instruction.instructionContent')}
            name="instruction"
            rules={[{ required: true, message: t('instruction.enterInstruction') }]}
          >
            <TextArea
              rows={4}
              placeholder={t('instruction.instructionPlaceholder')}
              showCount
              maxLength={3000}
            />
          </Form.Item>

          <div style={{ marginBottom: 24 }}>
            <div style={{ marginBottom: 8 }}>
              <span>{t('instruction.relatedQuestions')}</span>
              <span style={{ marginLeft: 8, fontSize: '12px', color: '#999' }}>(可选)</span>
            </div>
            <Form.List name="questions">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <div key={key} style={{ display: 'flex', marginBottom: 8, alignItems: 'flex-start' }}>
                      <Form.Item {...restField} name={[name, 'id']} hidden>
                        <Input />
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, 'question_text']}
                        style={{ flex: 1, marginBottom: 0, marginRight: 8 }}
                      >
                        <TextArea
                          placeholder={t('instruction.enterQuestion')}
                          rows={2}
                          showCount
                          maxLength={500}
                        />
                      </Form.Item>
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => remove(name)}
                        style={{ marginTop: 4 }}
                      />
                    </div>
                  ))}
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    icon={<PlusOutlined />}
                    style={{ width: '100%' }}
                  >
                    {t('instruction.addQuestion')}
                  </Button>
                </>
              )}
            </Form.List>
          </div>

          <Divider />

          <div style={{ textAlign: 'right' }}>
            <Space>
              {isEditMode && editingInstruction && (
                <Button 
                  icon={<SyncOutlined />}
                  onClick={() => {
                    setModalVisible(false);
                    // 从原始指令列表中查找对应的指令
                    const originalInstruction = instructions.find(inst => inst.id === editingInstruction.id);
                    if (originalInstruction) {
                      handleSync(originalInstruction);
                    }
                  }}
                >
                  {t('instruction.syncToRemote')}
                </Button>
              )}
              <Button onClick={() => setModalVisible(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
                {isEditMode ? t('common.update') : t('common.create')}
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>

      {/* 导入JSON模态框 */}
      <Modal
        title={t('instruction.import')}
        open={importVisible}
        onCancel={() => { setImportVisible(false); setImportFile(null); setImportInputKey(k => k + 1); }}
        footer={null}
        width={600}
        centered
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <input
            type="file"
            accept="application/json,.json"
            key={importInputKey}
            onChange={(e) => {
              const f = e.target.files && e.target.files[0] ? e.target.files[0] : null;
              setImportFile(f);
            }}
          />
          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={() => { setImportVisible(false); setImportFile(null); setImportInputKey(k => k + 1); }}>
                {t('common.cancel')}
              </Button>
              <Button onClick={handleImportDefault} loading={importing}>
                {t('instruction.importDefault')}
              </Button>
              <Button type="primary" loading={importing} onClick={handleImportFromJson}>
                {t('instruction.import')}
              </Button>
            </Space>
          </div>
        </Space>
      </Modal>

      {/* 备份文件列表模态框（仅超级管理员） */}
      {isSuperAdmin && (
        <Modal
          title={t('instruction.backupFileList')}
          open={backupFilesVisible}
          onCancel={() => setBackupFilesVisible(false)}
          footer={[
            <Button key="close" onClick={() => setBackupFilesVisible(false)}>
              {t('common.close')}
            </Button>
          ]}
          width={800}
        >
          <div style={{ marginBottom: 16 }}>
            <Text type="secondary">{t('instruction.backupFileListDesc')}</Text>
          </div>

          {backupFiles.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
              <FileTextOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
              <div>{t('instruction.noBackupFiles')}</div>
            </div>
          ) : (
            <Table
              dataSource={backupFiles}
              rowKey="filename"
              pagination={false}
              size="small"
              columns={[
                {
                  title: t('instruction.fileName'),
                  dataIndex: 'filename',
                  key: 'filename',
                  render: (filename: string) => (
                    <Space>
                      <FileTextOutlined />
                      <Text code>{filename}</Text>
                    </Space>
                  ),
                },
                {
                  title: t('instruction.fileSize'),
                  dataIndex: 'size',
                  key: 'size',
                  render: (size: number) => (
                    <Text type="secondary">{(size / 1024).toFixed(1)} KB</Text>
                  ),
                },
                {
                  title: t('instruction.createdAt'),
                  dataIndex: 'created',
                  key: 'created',
                  render: (created: string) => (
                    <Text type="secondary">
                      {new Date(created).toLocaleString('zh-CN')}
                    </Text>
                  ),
                },
                {
                  title: t('common.actions'),
                  key: 'actions',
                  render: (_: unknown, record: any) => (
                    <Space>
                      <Tooltip title="下载JSON">
                        <Button
                          type="link"
                          icon={<DownloadOutlined />}
                          onClick={() => handleDownloadBackupFile(record.filename)}
                        >
                          JSON
                        </Button>
                      </Tooltip>
                      <Tooltip title="下载Excel">
                        <Button
                          type="link"
                          icon={<FileExcelOutlined />}
                          onClick={() => handleDownloadBackupFileAsExcel(record.filename)}
                        >
                          Excel
                        </Button>
                      </Tooltip>
                    </Space>
                  ),
                },
              ]}
            />
          )}
        </Modal>
      )}
    </div>
  );
};

export default InstructionManager;
